import * as Sentry from "@sentry/react";
import { z } from "zod";
import { type StateFilters, stateSearchSchema } from "@/schemas/stateSearch";
import type { Filter } from "@/types/SuperFilter";

const STORAGE_PREFIX = "wai:filters";

/** Save filter state to localStorage. Errors are reported to Sentry (e.g. QuotaExceededError). */
export function savePersistedFilters(key: string, data: unknown): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}:${key}`, JSON.stringify(data));
  } catch (error) {
    Sentry.captureException(error, { tags: { feature: "filterPersistence" } });
  }
}

const filterItemSchema = z.object({
  key: z.union([z.string(), z.number(), z.boolean()]),
  label: z.string(),
});
const filterSchema = z.object({
  component: filterItemSchema,
  values: z.array(filterItemSchema),
});
const savedFiltersSchema = z.array(filterSchema);

/**
 * Zod-validated read for Filter[]-shaped persisted state (written by SuperFilter).
 * Returns null if the stored value is absent, invalid, or corrupt.
 */
export function getSavedFilters(key: string): Filter[] | null {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}:${key}`);
    if (!raw) return null;
    return savedFiltersSchema.parse(JSON.parse(raw)) as Filter[];
  } catch (error) {
    Sentry.captureException(error, { tags: { feature: "filterPersistence" } });
    return null;
  }
}

// Derive defaults directly from the schema so this stays correct when fields change.
const DEFAULT_STATE_FILTERS = stateSearchSchema.parse({}).state;

/**
 * Returns true if there are any active (non-default) filter chips worth restoring for the Tilstand page.
 * Page number is always reset to 1 on restore and is not checked here.
 */
export function hasActiveStateFilters(state: StateFilters | undefined): boolean {
  if (!state) return false;
  return Object.entries(state).some(([key, value]) => {
    if (key === "page" || value === undefined) return false;
    if (Array.isArray(value)) return value.length > 0;
    return value !== DEFAULT_STATE_FILTERS[key];
  });
}
